import { useMemo, useRef, useState } from 'react'
import Editor from '@monaco-editor/react'
import {
  ChevronDown,
  ChevronRight,
  FileCode2,
  FilePlus2,
  FolderClosed,
  FolderOpen,
  FolderPlus,
  FolderUp,
  PackagePlus,
  Info,
  PanelLeftOpen,
  Save,
  Trash2,
  Upload,
  X,
} from 'lucide-react'
import { useTheme } from '@/store/theme'

/**
 * Project workspace (spec section 19, Phase 5).
 *
 * <p>Files are imported from the machine through the browser's own file
 * pickers, so they can be read and edited here before the agent's `file.*`
 * tools are wired up. Where the browser supports the File System Access API
 * the original handle is kept and Save writes back to the same file; where it
 * does not, Save falls back to a download, because silently editing a copy
 * that goes nowhere would be worse than saying so.
 */

interface Node {
  id: string
  name: string
  kind: 'file' | 'folder'
  /** null at the top level. */
  parentId: string | null
  /** Present only for files opened through the File System Access API. */
  handle?: FileSystemFileHandle
  /**
   * Present on folders opened through the directory picker. Having one is what
   * lets a new file inside this folder be written to the real disk.
   */
  dirHandle?: FileSystemDirectoryHandle
}

/** The parts of the File System Access API this page uses. */
interface FileSystemFileHandle {
  kind: 'file'
  name: string
  createWritable: () => Promise<{ write: (data: string) => Promise<void>; close: () => Promise<void> }>
  getFile: () => Promise<File>
}

interface FileSystemDirectoryHandle {
  kind: 'directory'
  name: string
  entries: () => AsyncIterableIterator<[string, FileSystemFileHandle | FileSystemDirectoryHandle]>
  getFileHandle: (name: string, options?: { create?: boolean }) => Promise<FileSystemFileHandle>
  getDirectoryHandle: (
    name: string,
    options?: { create?: boolean },
  ) => Promise<FileSystemDirectoryHandle>
}

declare global {
  interface Window {
    showOpenFilePicker?: (options?: { multiple?: boolean }) => Promise<FileSystemFileHandle[]>
    showDirectoryPicker?: (options?: {
      mode?: 'read' | 'readwrite'
    }) => Promise<FileSystemDirectoryHandle>
  }
}

/** A new file or folder being named, and where it will be put. */
interface Draft {
  kind: 'file' | 'folder'
  parentId: string | null
}

/**
 * Never walked into. A project folder's dependencies and history are tens of
 * thousands of files, and reading them would hang the tab for no benefit.
 */
const SKIP_DIRS = new Set(['node_modules', '.git', 'dist', 'build', 'target', '.next', 'vendor'])

/** Anything larger is refused: this is an editor, not a hex viewer. */
const MAX_BYTES = 2 * 1024 * 1024

let seq = 0
const nextId = () => `n${++seq}`

export function ProjectsPage() {
  const [nodes, setNodes] = useState<Node[]>([])
  const [openId, setOpenId] = useState<string | null>(null)
  const [contents, setContents] = useState<Record<string, string>>({})
  const [saved, setSaved] = useState<Record<string, string>>({})
  const [expanded, setExpanded] = useState<Set<string>>(new Set())
  const [collapsed, setCollapsed] = useState(true)
  const [notice, setNotice] = useState<string | null>(null)
  const [draft, setDraft] = useState<Draft | null>(null)
  const [newProject, setNewProject] = useState(false)

  const filesInput = useRef<HTMLInputElement>(null)
  const folderInput = useRef<HTMLInputElement>(null)
  const theme = useTheme((s) => s.theme)

  // Creating only makes sense inside a project: a file with nowhere to belong
  // cannot be saved anywhere, so the actions stay off until something is in.
  const hasProject = nodes.length > 0
  const CREATE_DISABLED = 'Start or import a project first — a new file needs somewhere to live'

  const openFile = nodes.find((n) => n.id === openId) ?? null
  const dirty = openFile ? contents[openFile.id] !== saved[openFile.id] : false

  const byParent = useMemo(() => {
    const map = new Map<string | null, Node[]>()
    for (const node of nodes) {
      const list = map.get(node.parentId) ?? []
      list.push(node)
      map.set(node.parentId, list)
    }
    // Folders first, then alphabetical — the order every file tree uses.
    for (const list of map.values()) {
      list.sort((a, b) =>
        a.kind === b.kind ? a.name.localeCompare(b.name) : a.kind === 'folder' ? -1 : 1,
      )
    }
    return map
  }, [nodes])

  /** Every folder a new item could go into, deepest path spelled out. */
  const folderOptions = useMemo(() => {
    const out: { id: string | null; label: string; writable: boolean }[] = [
      { id: null, label: 'Workspace root', writable: false },
    ]
    const walk = (parentId: string | null, prefix: string) => {
      for (const node of nodes.filter((x) => x.parentId === parentId && x.kind === 'folder')) {
        const label = `${prefix}/${node.name}`
        out.push({ id: node.id, label, writable: Boolean(node.dirHandle) })
        walk(node.id, label)
      }
    }
    walk(null, '')
    return out
  }, [nodes])

  /** Adds one imported file at `path`, creating any folders it sits under. */
  const addAtPath = (
    path: string,
    text: string,
    handle: FileSystemFileHandle | undefined,
    draft: { nodes: Node[]; opened: Set<string> },
  ) => {
    const parts = path.split('/').filter(Boolean)
    const fileName = parts.pop()!
    let parentId: string | null = null

    for (const segment of parts) {
      const existing = draft.nodes.find(
        (n) => n.parentId === parentId && n.name === segment && n.kind === 'folder',
      )
      if (existing) {
        parentId = existing.id
      } else {
        const folder: Node = { id: nextId(), name: segment, kind: 'folder', parentId }
        draft.nodes.push(folder)
        draft.opened.add(folder.id)
        parentId = folder.id
      }
    }

    const file: Node = { id: nextId(), name: fileName, kind: 'file', parentId, handle }
    draft.nodes.push(file)
    return { file, text }
  }

  /** Reads a batch of picked files into the tree. */
  const ingest = async (picked: { path: string; file: File; handle?: FileSystemFileHandle }[]) => {
    const draft = { nodes: [...nodes], opened: new Set(expanded) }
    const nextContents: Record<string, string> = {}
    let firstFileId: string | null = null
    let skipped = 0

    for (const item of picked) {
      if (item.file.size > MAX_BYTES) {
        skipped++
        continue
      }
      const text = await item.file.text()
      // A NUL byte is the cheapest reliable sign that this is not text.
      if (text.includes('\u0000')) {
        skipped++
        continue
      }
      const added = addAtPath(item.path, text, item.handle, draft)
      nextContents[added.file.id] = text
      firstFileId ??= added.file.id
    }

    setNodes(draft.nodes)
    setExpanded(draft.opened)
    setContents((prev) => ({ ...prev, ...nextContents }))
    setSaved((prev) => ({ ...prev, ...nextContents }))
    if (firstFileId) setOpenId(firstFileId)
    // Importing is exactly when the tree becomes worth looking at.
    setCollapsed(false)
    setNotice(
      skipped > 0
        ? `Imported ${picked.length - skipped} file${picked.length - skipped === 1 ? '' : 's'}. Skipped ${skipped} that were binary or over 2 MB.`
        : null,
    )
  }

  const importFiles = async () => {
    // The File System Access API gives a handle that Save can write back to.
    if (window.showOpenFilePicker) {
      try {
        const handles = await window.showOpenFilePicker({ multiple: true })
        await ingest(
          await Promise.all(
            handles.map(async (handle) => ({
              path: handle.name,
              file: await handle.getFile(),
              handle,
            })),
          ),
        )
        return
      } catch (error) {
        // An abort is the user closing the picker, not a failure.
        if ((error as DOMException)?.name === 'AbortError') return
      }
    }
    filesInput.current?.click()
  }

  /**
   * Opens a real folder, keeping every handle on the way down.
   *
   * <p>Those handles are the whole point: with them a file created inside this
   * project is written to the actual directory on disk, not held in the tab.
   */
  const importFolder = async () => {
    if (!window.showDirectoryPicker) {
      // No directory handles here, so the fallback input can only ever produce
      // copies. The create form says as much when that is all we have.
      folderInput.current?.click()
      return
    }

    let root: FileSystemDirectoryHandle
    try {
      root = await window.showDirectoryPicker({ mode: 'readwrite' })
    } catch (error) {
      if ((error as DOMException)?.name === 'AbortError') return
      setNotice('Could not open that folder.')
      return
    }

    const collected: Node[] = []
    const texts: Record<string, string> = {}
    let skipped = 0

    const walk = async (dir: FileSystemDirectoryHandle, parentId: string | null, depth: number) => {
      // Deep enough for a real project, shallow enough not to run away.
      if (depth > 6) return
      for await (const [name, handle] of dir.entries()) {
        if (handle.kind === 'directory') {
          if (SKIP_DIRS.has(name)) continue
          const folder: Node = {
            id: nextId(),
            name,
            kind: 'folder',
            parentId,
            dirHandle: handle,
          }
          collected.push(folder)
          await walk(handle, folder.id, depth + 1)
        } else {
          const file = await handle.getFile()
          if (file.size > MAX_BYTES) {
            skipped++
            continue
          }
          const text = await file.text()
          if (text.includes('\u0000')) {
            skipped++
            continue
          }
          const node: Node = { id: nextId(), name, kind: 'file', parentId, handle }
          collected.push(node)
          texts[node.id] = text
        }
      }
    }

    const rootNode: Node = {
      id: nextId(),
      name: root.name,
      kind: 'folder',
      parentId: null,
      dirHandle: root,
    }
    collected.push(rootNode)
    await walk(root, rootNode.id, 0)

    setNodes((prev) => [...prev, ...collected])
    setContents((prev) => ({ ...prev, ...texts }))
    setSaved((prev) => ({ ...prev, ...texts }))
    setExpanded((prev) => new Set(prev).add(rootNode.id))
    setCollapsed(false)
    const fileCount = collected.filter((n) => n.kind === 'file').length
    setNotice(
      `Opened ${root.name} — ${fileCount} file${fileCount === 1 ? '' : 's'}.` +
        (skipped ? ` Skipped ${skipped} that were binary or over 2 MB.` : '') +
        ' New files created here are written to this folder.',
    )
  }

  /**
   * Starts a project from nothing.
   *
   * <p>A browser cannot invent a folder out of thin air, so this asks where to
   * put it: you pick a parent directory, and the project folder is created
   * inside it on the real disk. That handle is what makes everything created
   * afterwards a real file rather than a scratch buffer.
   */
  const createProject = async (name: string) => {
    const trimmed = name.trim()
    if (!trimmed) return setNewProject(false)

    if (window.showDirectoryPicker) {
      let parent: FileSystemDirectoryHandle
      try {
        parent = await window.showDirectoryPicker({ mode: 'readwrite' })
      } catch (error) {
        // Closing the picker leaves the form up, so the name is not lost.
        if ((error as DOMException)?.name === 'AbortError') return
        setNotice('Could not open that location.')
        return
      }

      try {
        const dir = await parent.getDirectoryHandle(trimmed, { create: true })
        addProjectRoot(trimmed, dir)
        setNotice(
          `Created ${trimmed} in ${parent.name} on your disk. Files you add here are written to it.`,
        )
      } catch {
        setNotice(`Could not create ${trimmed} there. Check the folder is writable.`)
      }
      return
    }

    // No directory handles in this browser, so the project can only be a
    // workspace held in the tab. Said plainly rather than discovered later.
    addProjectRoot(trimmed, undefined)
    setNotice(
      `Created ${trimmed} in this tab only — this browser cannot create folders on disk. Use Chrome to work on real files.`,
    )
  }

  const addProjectRoot = (name: string, dirHandle: FileSystemDirectoryHandle | undefined) => {
    const root: Node = { id: nextId(), name, kind: 'folder', parentId: null, dirHandle }
    setNodes((prev) => [...prev, root])
    setExpanded((prev) => new Set(prev).add(root.id))
    setCollapsed(false)
    setNewProject(false)
  }

  /** Starts naming a new file or folder, opening the panel to show the field. */
  const startDraft = (kind: 'file' | 'folder', parentId: string | null = null) => {
    setCollapsed(false)
    if (parentId) setExpanded((prev) => new Set(prev).add(parentId))
    setDraft({ kind, parentId })
  }

  /**
   * Creates the drafted file or folder in the chosen location.
   *
   * <p>Where the chosen folder came from the directory picker it carries a
   * handle, and the item is created on the real disk. Where it did not, it is
   * held in the tab and the form has already said so.
   */
  const createFromDraft = async (name: string, parentId: string | null) => {
    const trimmed = name.trim()
    if (!draft || !trimmed) return setDraft(null)

    if (nodes.some((n) => n.parentId === parentId && n.name === trimmed)) {
      setNotice(`“${trimmed}” already exists there.`)
      return
    }

    const parent = parentId ? nodes.find((n) => n.id === parentId) : null
    const dir = parent?.dirHandle
    const node: Node = { id: nextId(), name: trimmed, kind: draft.kind, parentId }

    if (dir) {
      try {
        if (draft.kind === 'folder') {
          node.dirHandle = await dir.getDirectoryHandle(trimmed, { create: true })
        } else {
          node.handle = await dir.getFileHandle(trimmed, { create: true })
        }
        setNotice(`Created ${trimmed} in ${parent?.name} on disk.`)
      } catch {
        setNotice(`Could not write ${trimmed} to disk. Kept it in this tab instead.`)
      }
    } else {
      setNotice(`Created ${trimmed} in this tab only — it is not written to disk.`)
    }

    setNodes((prev) => [...prev, node])
    if (parentId) setExpanded((prev) => new Set(prev).add(parentId))
    if (node.kind === 'folder') {
      setExpanded((prev) => new Set(prev).add(node.id))
    } else {
      // A new file starts empty and unmodified, so Save stays off until it is
      // actually typed into.
      setContents((prev) => ({ ...prev, [node.id]: '' }))
      setSaved((prev) => ({ ...prev, [node.id]: '' }))
      setOpenId(node.id)
    }
    setDraft(null)
  }

  const remove = (id: string) => {
    // Takes the subtree with it, so deleting a folder cannot orphan its files.
    const doomed = new Set<string>([id])
    let grew = true
    while (grew) {
      grew = false
      for (const node of nodes) {
        if (node.parentId && doomed.has(node.parentId) && !doomed.has(node.id)) {
          doomed.add(node.id)
          grew = true
        }
      }
    }
    setNodes((prev) => prev.filter((n) => !doomed.has(n.id)))
    if (openId && doomed.has(openId)) setOpenId(null)
  }

  const save = async () => {
    if (!openFile) return
    const text = contents[openFile.id] ?? ''

    if (openFile.handle) {
      const writable = await openFile.handle.createWritable()
      await writable.write(text)
      await writable.close()
      setSaved((prev) => ({ ...prev, [openFile.id]: text }))
      setNotice(`Saved ${openFile.name} to disk.`)
      return
    }

    // No handle: this file arrived as a copy, so the honest option is to hand
    // the edited version back rather than pretend it was written in place.
    const url = URL.createObjectURL(new Blob([text], { type: 'text/plain' }))
    const link = document.createElement('a')
    link.href = url
    link.download = openFile.name
    link.click()
    URL.revokeObjectURL(url)
    setSaved((prev) => ({ ...prev, [openFile.id]: text }))
    setNotice(`Downloaded ${openFile.name}. This browser cannot write back to the original file.`)
  }

  return (
    <div className="relative flex h-full">
      {/* Hidden pickers, used where the File System Access API is unavailable. */}
      <input
        ref={filesInput}
        type="file"
        multiple
        className="hidden"
        onChange={(e) => {
          const files = Array.from(e.target.files ?? [])
          if (files.length) void ingest(files.map((file) => ({ path: file.name, file })))
          e.target.value = ''
        }}
      />
      <input
        ref={folderInput}
        type="file"
        multiple
        className="hidden"
        // Not in the React types, but supported wherever this app runs.
        {...({ webkitdirectory: '', directory: '' } as Record<string, string>)}
        onChange={(e) => {
          const files = Array.from(e.target.files ?? [])
          if (files.length) {
            void ingest(
              files.map((file) => ({
                path: (file as File & { webkitRelativePath?: string }).webkitRelativePath || file.name,
                file,
              })),
            )
          }
          e.target.value = ''
        }}
      />

      {collapsed ? (
        // Minimised: a rail of actions. The first one opens the panel again.
        <div className="flex w-11 shrink-0 flex-col items-center gap-1 border-r border-border bg-surface-raised py-2">
          <RailAction
            label="Show files"
            onClick={() => setCollapsed(false)}
            icon={<PanelLeftOpen size={16} />}
          />
          <RailAction
            label="New project"
            onClick={() => { setCollapsed(false); setNewProject(true) }}
            icon={<PackagePlus size={16} />}
          />
          <RailAction
            label="New file"
            onClick={() => startDraft('file')}
            disabled={!hasProject}
            disabledReason={CREATE_DISABLED}
            icon={<FilePlus2 size={16} />}
          />
          <RailAction
            label="New folder"
            onClick={() => startDraft('folder')}
            disabled={!hasProject}
            disabledReason={CREATE_DISABLED}
            icon={<FolderPlus size={16} />}
          />
          {nodes.length > 0 && (
            <span className="mt-1 rounded bg-accent/15 px-1.5 py-0.5 text-[10px] font-bold text-accent">
              {nodes.filter((n) => n.kind === 'file').length}
            </span>
          )}
        </div>
      ) : (
        <>
          {/* Below md the panel would leave the editor 144px wide, so it floats
              over it instead and this dismisses it. */}
          <button
            type="button"
            aria-label="Hide files"
            onClick={() => setCollapsed(true)}
            className="absolute inset-0 z-10 bg-black/40 md:hidden"
          />
          <aside className="absolute inset-y-0 left-0 z-20 flex w-[280px] max-w-[85%] shrink-0 flex-col border-r border-border bg-surface-raised shadow-float md:static md:w-64 md:max-w-none md:shadow-none">
          <div className="flex items-center gap-1 px-3 py-2.5">
            <h1 className="flex-1 text-[13px] font-bold">Files</h1>
            {/* These two create; importing from the machine is below the tree. */}
            <RailAction
              label="New file"
              onClick={() => startDraft('file')}
              disabled={!hasProject}
              disabledReason={CREATE_DISABLED}
              icon={<FilePlus2 size={15} />}
            />
            <RailAction
              label="New folder"
              onClick={() => startDraft('folder')}
              disabled={!hasProject}
              disabledReason={CREATE_DISABLED}
              icon={<FolderPlus size={15} />}
            />
            <RailAction
              label="Hide files"
              onClick={() => setCollapsed(true)}
              icon={<ChevronLeftIcon />}
            />
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto px-1.5 pb-2">
            {newProject && (
              <NewProjectForm
                onCommit={(name) => void createProject(name)}
                onCancel={() => setNewProject(false)}
              />
            )}
            {draft && (
              <CreateForm
                kind={draft.kind}
                folders={folderOptions}
                defaultParentId={draft.parentId}
                onCommit={(name, parentId) => void createFromDraft(name, parentId)}
                onCancel={() => setDraft(null)}
              />
            )}
            <Tree
              parentId={null}
              depth={0}
              byParent={byParent}
              expanded={expanded}
              openId={openId}
              onToggle={(id) =>
                setExpanded((prev) => {
                  const next = new Set(prev)
                  if (next.has(id)) next.delete(id)
                  else next.add(id)
                  return next
                })
              }
              onOpen={setOpenId}
              onRemove={remove}
              onDraft={startDraft}
            />

            {nodes.length === 0 && !draft && (
              <p className="px-2.5 py-3 text-[12px] leading-relaxed text-content-muted">
                No project open. Start a new one below, or import files and folders from your
                computer.
              </p>
            )}
          </div>

          <div className="border-t border-border p-2">
            <button
              type="button"
              onClick={() => setNewProject(true)}
              className="mb-1.5 flex w-full items-center justify-center gap-1.5 rounded-lg bg-accent px-2 py-1.5 text-[12px] font-bold text-accent-content transition hover:opacity-90"
            >
              <PackagePlus size={14} /> New project
            </button>
            <div className="flex gap-1.5">
              <button
                type="button"
                onClick={importFiles}
                className="flex flex-1 items-center justify-center gap-1.5 rounded-lg border border-border px-2 py-1.5 text-[12px] font-semibold transition hover:border-accent hover:text-accent"
              >
                <Upload size={14} /> Import files
              </button>
              <button
                type="button"
                onClick={() => void importFolder()}
                className="flex flex-1 items-center justify-center gap-1.5 rounded-lg border border-border px-2 py-1.5 text-[12px] font-semibold transition hover:border-accent hover:text-accent"
              >
                <FolderUp size={14} /> Import folder
              </button>
            </div>
            <p className="mt-2 px-0.5 text-[11px] leading-relaxed text-content-muted">
              Import first, then the icons above add to the project. Files opened through the
              picker save back to disk; anything else downloads on Save.
            </p>
          </div>
          </aside>
        </>
      )}

      <div className="flex min-w-0 flex-1 flex-col">
        <div className="flex items-center gap-2 border-b border-border px-4 py-2">
          <FileCode2 size={15} className="shrink-0 text-content-muted" />
          <span className="truncate font-mono text-[12px]">{openFile?.name ?? 'No file open'}</span>
          {dirty && <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-accent" title="Unsaved changes" />}

          {openFile && (
            <div className="ml-auto flex items-center gap-1">
              <button
                type="button"
                onClick={() => void save()}
                disabled={!dirty}
                className="flex items-center gap-1.5 rounded-lg border border-border px-2.5 py-1 text-[12px] font-semibold transition hover:border-accent hover:text-accent disabled:opacity-40"
              >
                <Save size={13} /> Save
              </button>
              <button
                type="button"
                onClick={() => setOpenId(null)}
                aria-label={`Close ${openFile.name}`}
                className="rounded p-1 text-content-muted transition hover:text-content"
              >
                <X size={14} />
              </button>
            </div>
          )}
        </div>

        {notice && (
          <p className="flex items-start gap-2 border-b border-border bg-surface-sunken px-4 py-2 text-[12px] text-content-muted">
            <Info size={14} className="mt-0.5 shrink-0" />
            <span className="flex-1">{notice}</span>
            <button type="button" onClick={() => setNotice(null)} aria-label="Dismiss">
              <X size={13} />
            </button>
          </p>
        )}

        {openFile ? (
          <Editor
            height="100%"
            // Monaco picks the language from the path's extension.
            path={openFile.name}
            value={contents[openFile.id] ?? ''}
            onChange={(value) => setContents((prev) => ({ ...prev, [openFile.id]: value ?? '' }))}
            theme={theme === 'dark' ? 'vs-dark' : 'light'}
            options={{ minimap: { enabled: false }, fontSize: 13, padding: { top: 14 } }}
          />
        ) : (
          <div className="flex flex-1 items-center justify-center p-8">
            <div className="max-w-md rounded-xl border border-border bg-surface-raised p-5 text-sm">
              <div className="mb-2 flex items-center gap-2 font-bold">
                <Info size={16} />
                Project mode is in progress
              </div>
              <p className="leading-relaxed text-content-muted">
                Import files from your computer to read and edit them here. The git panel and
                terminal are built but not yet connected to the agent&rsquo;s
                <code className="mx-1 rounded bg-surface-sunken px-1 py-0.5 text-xs">file.*</code>
                and
                <code className="mx-1 rounded bg-surface-sunken px-1 py-0.5 text-xs">git.*</code>
                tools, so ask NevTan in chat to build or run anything &mdash; those paths are live
                and go through the same approval gate.
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => { setCollapsed(false); setNewProject(true) }}
                  className="rounded-lg bg-accent px-3 py-1.5 text-xs font-bold text-accent-content transition hover:opacity-90"
                >
                  New project
                </button>
                <button
                  type="button"
                  onClick={importFiles}
                  className="rounded-lg border border-border px-3 py-1.5 text-xs font-semibold transition hover:border-accent hover:text-accent"
                >
                  Import files
                </button>
                <button
                  type="button"
                  onClick={() => void importFolder()}
                  className="rounded-lg border border-border px-3 py-1.5 text-xs font-semibold transition hover:border-accent hover:text-accent"
                >
                  Import a folder
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

/** lucide has no left-facing panel icon at this weight, so the chevron does. */
function ChevronLeftIcon() {
  return <ChevronRight size={15} className="rotate-180" />
}

function RailAction({
  label,
  icon,
  onClick,
  disabled,
  /** Explains the disabled state, so the control is not just dead on hover. */
  disabledReason,
}: {
  label: string
  icon: React.ReactNode
  onClick: () => void
  disabled?: boolean
  disabledReason?: string
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={disabled ? (disabledReason ?? label) : label}
      aria-label={label}
      className="rounded p-1.5 text-content-muted transition enabled:hover:bg-surface-sunken enabled:hover:text-content disabled:cursor-not-allowed disabled:opacity-35"
    >
      {icon}
    </button>
  )
}

/** One level of the tree, recursing into folders that are open. */
function Tree({
  parentId,
  depth,
  byParent,
  expanded,
  openId,
  onToggle,
  onOpen,
  onRemove,
  onDraft,
}: {
  parentId: string | null
  depth: number
  byParent: Map<string | null, Node[]>
  expanded: Set<string>
  openId: string | null
  onToggle: (id: string) => void
  onOpen: (id: string) => void
  onRemove: (id: string) => void
  onDraft: (kind: 'file' | 'folder', parentId: string | null) => void
}) {
  const children = byParent.get(parentId) ?? []

  return (
    <ul>
      {children.map((node) => {
        const isOpen = expanded.has(node.id)
        return (
          <li key={node.id}>
            <div
              className={`group flex items-center gap-1 rounded-md pr-1 ${
                openId === node.id ? 'bg-accent/10 text-accent' : 'hover:bg-surface-sunken'
              }`}
              style={{ paddingLeft: depth * 12 + 4 }}
            >
              <button
                type="button"
                onClick={() => (node.kind === 'folder' ? onToggle(node.id) : onOpen(node.id))}
                className="flex min-w-0 flex-1 items-center gap-1.5 py-1.5 text-left text-[13px]"
              >
                {node.kind === 'folder' ? (
                  <>
                    {isOpen ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
                    {isOpen ? <FolderOpen size={14} /> : <FolderClosed size={14} />}
                  </>
                ) : (
                  <FileCode2 size={14} className="ml-[13px] shrink-0 text-content-muted" />
                )}
                <span className="truncate">{node.name}</span>
              </button>

              {node.kind === 'folder' && (
                <span className="opacity-0 transition focus-within:opacity-100 group-hover:opacity-100">
                  <RailAction
                    label={`New file in ${node.name}`}
                    onClick={() => onDraft('file', node.id)}
                    icon={<FilePlus2 size={13} />}
                  />
                </span>
              )}
              <button
                type="button"
                onClick={() => onRemove(node.id)}
                aria-label={`Remove ${node.name}`}
                title="Remove from the workspace"
                className="rounded p-1 text-content-muted opacity-0 transition hover:text-danger focus:opacity-100 group-hover:opacity-100"
              >
                <Trash2 size={13} />
              </button>
            </div>

            {node.kind === 'folder' && isOpen && (
              <Tree
                parentId={node.id}
                depth={depth + 1}
                byParent={byParent}
                expanded={expanded}
                openId={openId}
                onToggle={onToggle}
                onOpen={onOpen}
                onRemove={onRemove}
                onDraft={onDraft}
              />
            )}
          </li>
        )
      })}

    </ul>
  )
}

/**
 * Names a brand new project.
 *
 * <p>The location is asked for after the name, through the browser's own
 * directory picker — a page cannot be handed a path, so choosing the parent
 * folder has to be a deliberate gesture by the person at the keyboard.
 */
function NewProjectForm({
  onCommit,
  onCancel,
}: {
  onCommit: (name: string) => void
  onCancel: () => void
}) {
  const [name, setName] = useState('')
  const onDisk = typeof window !== 'undefined' && Boolean(window.showDirectoryPicker)

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault()
        if (name.trim()) onCommit(name)
      }}
      className="mb-2 rounded-lg border border-accent/40 bg-surface p-2.5"
    >
      <p className="mb-2 flex items-center gap-1.5 text-[12px] font-bold">
        <PackagePlus size={13} />
        New project
      </p>

      <label className="block text-[11px] font-semibold text-content-muted">
        Project name
        <input
          autoFocus
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') onCancel()
          }}
          placeholder="my-app"
          className="mt-1 w-full rounded border border-border bg-surface-sunken px-1.5 py-1 text-[12px] font-normal text-content outline-none focus:border-accent"
        />
      </label>

      <p className="mt-2 text-[11px] leading-relaxed text-content-muted">
        {onDisk
          ? 'You’ll choose where to put it next, and the folder is created there on your disk.'
          : 'This browser cannot create folders on disk, so the project is kept in this tab. Chrome can create it for real.'}
      </p>

      <div className="mt-2 flex gap-1.5">
        <button
          type="submit"
          disabled={!name.trim()}
          className="flex-1 rounded-md bg-accent px-2 py-1 text-[12px] font-bold text-accent-content transition hover:opacity-90 disabled:opacity-40"
        >
          {onDisk ? 'Choose location…' : 'Create'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="rounded-md border border-border px-2 py-1 text-[12px] font-semibold transition hover:text-content"
        >
          Cancel
        </button>
      </div>
    </form>
  )
}

/**
 * Asks for the name and the location before anything is created.
 *
 * <p>The location matters: only a folder opened through the directory picker
 * can take a real file, so the form states which of the two is about to
 * happen rather than leaving it to be discovered afterwards.
 */
function CreateForm({
  kind,
  folders,
  defaultParentId,
  onCommit,
  onCancel,
}: {
  kind: 'file' | 'folder'
  folders: { id: string | null; label: string; writable: boolean }[]
  defaultParentId: string | null
  onCommit: (name: string, parentId: string | null) => void
  onCancel: () => void
}) {
  const [name, setName] = useState('')
  const [parentId, setParentId] = useState<string | null>(defaultParentId)
  const target = folders.find((f) => f.id === parentId)

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault()
        if (name.trim()) onCommit(name, parentId)
      }}
      className="mb-2 rounded-lg border border-accent/40 bg-surface p-2.5"
    >
      <p className="mb-2 flex items-center gap-1.5 text-[12px] font-bold">
        {kind === 'folder' ? <FolderPlus size={13} /> : <FilePlus2 size={13} />}
        New {kind}
      </p>

      <label className="block text-[11px] font-semibold text-content-muted">
        Location
        <select
          value={parentId ?? ''}
          onChange={(e) => setParentId(e.target.value || null)}
          className="mt-1 w-full rounded border border-border bg-surface-sunken px-1.5 py-1 text-[12px] font-normal text-content outline-none focus:border-accent"
        >
          {folders.map((folder) => (
            <option key={folder.id ?? 'root'} value={folder.id ?? ''}>
              {folder.label}
              {folder.writable ? '' : ' (this tab only)'}
            </option>
          ))}
        </select>
      </label>

      <label className="mt-2 block text-[11px] font-semibold text-content-muted">
        Name
        <input
          autoFocus
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') onCancel()
          }}
          placeholder={kind === 'folder' ? 'components' : 'App.tsx'}
          className="mt-1 w-full rounded border border-border bg-surface-sunken px-1.5 py-1 text-[12px] font-normal text-content outline-none focus:border-accent"
        />
      </label>

      <p className="mt-2 text-[11px] leading-relaxed text-content-muted">
        {target?.writable
          ? `Written to ${target.label} on your disk.`
          : 'Kept in this tab only — this folder was not opened with write access.'}
      </p>

      <div className="mt-2 flex gap-1.5">
        <button
          type="submit"
          disabled={!name.trim()}
          className="flex-1 rounded-md bg-accent px-2 py-1 text-[12px] font-bold text-accent-content transition hover:opacity-90 disabled:opacity-40"
        >
          Create
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="rounded-md border border-border px-2 py-1 text-[12px] font-semibold transition hover:text-content"
        >
          Cancel
        </button>
      </div>
    </form>
  )
}
